(() => {
  class LophielBrandCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.carouselBound === 'true') return;
      this.dataset.carouselBound = 'true';
      this.slides = Array.from(this.querySelectorAll('[data-brand-slide]'));
      this.status = this.querySelector('[data-brand-status]');
      this.autoplayInterval = Number(this.dataset.autoplayInterval) || 6500;
      this.autoplayTimer = null;
      this.isVisible = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleMouseEnter = this.stopAutoplay.bind(this);
      this.handleMouseLeave = this.startAutoplay.bind(this);
      this.handleFocusIn = this.stopAutoplay.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.motionQuery.addEventListener('change', this.handleMotionChange);
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting;
        if (this.isVisible) this.startAutoplay();
        else this.stopAutoplay();
      }, { threshold: 0.35 });
      this.visibilityObserver.observe(this);
      this.render(false);
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.motionQuery?.removeEventListener('change', this.handleMotionChange);
      this.visibilityObserver?.disconnect();
      delete this.dataset.carouselBound;
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-brand-prev]')) {
        this.goTo(this.activeIndex - 1);
        return;
      }

      if (target.closest('[data-brand-next]')) {
        this.goTo(this.activeIndex + 1);
        return;
      }

      const slide = target.closest('[data-brand-slide]');
      if (slide && slide.dataset.position !== 'active') {
        this.goTo(Number(slide.dataset.brandSlideIndex));
      }
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1);
      }
    }

    handleBlockSelect(event) {
      this.stopAutoplay();
      const slide = event.target.closest('[data-brand-slide]');
      if (slide) this.goTo(Number(slide.dataset.brandSlideIndex), false);
    }

    handleFocusOut(event) {
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
      this.startAutoplay();
    }

    handleVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }

    handleMotionChange() {
      if (this.motionQuery.matches) this.stopAutoplay();
      else this.startAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (
        this.slides.length < 2 ||
        !this.isVisible ||
        this.motionQuery.matches ||
        document.hidden ||
        this.matches(':hover') ||
        this.contains(document.activeElement)
      ) return;

      this.autoplayTimer = window.setInterval(() => {
        this.goTo(this.activeIndex + 1, false);
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    goTo(index, announce = true) {
      const count = this.slides.length;
      if (!count) return;
      this.activeIndex = ((index % count) + count) % count;
      this.render(announce);
    }

    render(announce) {
      const count = this.slides.length;
      if (!count) return;

      this.slides.forEach((slide, index) => {
        const relativeIndex = (index - this.activeIndex + count) % count;
        let position = 'hidden';

        if (relativeIndex === 0) position = 'active';
        else if (relativeIndex === 1) position = 'next';
        else if (relativeIndex === count - 1) position = 'prev';

        const isActive = position === 'active';
        slide.dataset.position = position;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      if (announce && this.status) {
        const title = this.slides[this.activeIndex]?.dataset.brandTitle || '';
        this.status.textContent = `Chapter ${this.activeIndex + 1} of ${count}: ${title}`;
      }
    }
  }

  if (!customElements.get('lophiel-brand-carousel')) {
    customElements.define('lophiel-brand-carousel', LophielBrandCarousel);
  }
})();
